import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, ArrowRight, CheckCircle, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pwd.length < 6) return setErr('La password deve avere almeno 6 caratteri');
    if (pwd !== pwd2) return setErr('Le password non coincidono');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#09090B] pt-24 flex items-center justify-center px-4" data-testid="reset-success">
        <div className="max-w-md w-full text-center">
          <CheckCircle size={48} className="text-emerald-400 mx-auto mb-4" />
          <h1 className="text-2xl md:text-3xl tracking-tight font-medium text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Password aggiornata
          </h1>
          <p className="text-sm text-zinc-500 mb-6 leading-relaxed">Puoi ora accedere con la nuova password.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] pt-24 flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#C5A059] mb-3">Sicurezza</p>
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Reimposta password
          </h1>
          <p className="text-sm text-zinc-500">Scegli una nuova password per il tuo account AeroDeals.</p>
        </div>
        <form onSubmit={submit} className="bg-zinc-900/50 border border-zinc-800 p-6 space-y-5">
          <div>
            <Label className="text-zinc-400 text-xs mb-1.5 block">Nuova password</Label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <Input type={showPwd ? 'text' : 'password'} value={pwd} onChange={e => setPwd(e.target.value)} placeholder="Almeno 6 caratteri" minLength={6} required className="pl-10 pr-10 bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 h-11" data-testid="reset-pwd" />
              <button type="button" onClick={() => setShowPwd(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#C5A059] transition-colors" title={showPwd ? 'Nascondi password' : 'Mostra password'} aria-label={showPwd ? 'Nascondi password' : 'Mostra password'} data-testid="toggle-reset-pwd-visibility">
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs mb-1.5 block">Conferma password</Label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <Input type={showPwd ? 'text' : 'password'} value={pwd2} onChange={e => setPwd2(e.target.value)} placeholder="Ripeti password" minLength={6} required className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder-zinc-600 h-11" data-testid="reset-pwd2" />
            </div>
          </div>
          {err && <p className="text-red-400 text-xs flex items-center gap-1" data-testid="reset-error"><AlertTriangle size={12} /> {err}</p>}
          <button type="submit" disabled={loading} className="w-full bg-[#C5A059] text-black py-3 text-sm font-medium hover:bg-[#D4B271] transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="reset-submit">
            {loading ? 'Aggiornamento...' : 'Imposta nuova password'}
            {!loading && <ArrowRight size={16} />}
          </button>
        </form>
      </div>
    </div>
  );
}
